import React, { useState, useEffect, useCallback, useRef } from 'react';
import ExcelPreviewTable from './viewers/ExcelPreviewTable';
import { useTranslation } from 'react-i18next';

interface ExcelEditorProps {
  csvContent?: string;
  blobUrl?: string;
  onContentChange: (content: string) => void;
  fileName: string;
  onDataLoaded?: (data: any[]) => void;
  isLoading?: boolean;
}

const ExcelEditor: React.FC<ExcelEditorProps> = ({
  csvContent,
  blobUrl,
  onContentChange,
  fileName,
  onDataLoaded,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const [isPreviewMode, setIsPreviewMode] = useState(true);
  const [textContent, setTextContent] = useState(csvContent || '');
  const [sheetData, setSheetData] = useState<any[]>([]);
  const [sheets, setSheets] = useState<{ [key: string]: any[] }>({});
  const [activeSheet, setActiveSheet] = useState<string>('');
  const [isLoadingSheet, setIsLoadingSheet] = useState(false);
  const [error, setError] = useState<string>('');
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);

  // Load Excel file from blob URL
  useEffect(() => {
    if (!blobUrl) return;

    const loadExcelFromBlob = async () => {
      try {
        setIsLoadingSheet(true);
        setError('');

        const response = await fetch(blobUrl);
        if (!response.ok) throw new Error('Failed to fetch file');

        const arrayBuffer = await response.arrayBuffer();

        // Try to parse as XLSX first
        try {
          const XLSX = await import('xlsx');
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });

          const sheetsData: { [key: string]: any[] } = {};
          workbook.SheetNames.forEach((sheetName) => {
            const worksheet = workbook.Sheets[sheetName];
            sheetsData[sheetName] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          });

          setSheets(sheetsData);
          const firstSheet = workbook.SheetNames[0] || '';
          setActiveSheet(firstSheet);

          if (sheetsData[firstSheet]) {
            setSheetData(sheetsData[firstSheet]);
            if (onDataLoaded) {
              onDataLoaded(sheetsData[firstSheet]);
            }
          }

          // Convert first sheet to CSV for edit mode
          const csvContent = sheetsData[firstSheet]
            .map((row: any[]) => row.map((cell) => `"${cell ?? ''}"`).join(','))
            .join('\n');
          setTextContent(csvContent);
        } catch (xlsxError) {
          // Fallback: treat as CSV
          const text = new TextDecoder().decode(arrayBuffer);
          const rows = text.split('\n').map((row) =>
            row.split(',').map((cell) => cell.trim())
          );
          setSheets({ 'Data': rows });
          setActiveSheet('Data');
          setSheetData(rows);
          setTextContent(text);
          if (onDataLoaded) {
            onDataLoaded(rows);
          }
        }
      } catch (err) {
        console.error('Error loading Excel from blob:', err);
        setError(err instanceof Error ? err.message : 'Failed to load spreadsheet');
      } finally {
        setIsLoadingSheet(false);
      }
    };

    loadExcelFromBlob();
  }, [blobUrl, onDataLoaded]);

  // Sync CSV content from props
  useEffect(() => {
    if (csvContent && !blobUrl) {
      setTextContent(csvContent);
      const lines = csvContent.split('\n').filter((line) => line.trim());
      const data = lines.map((line) =>
        line.split(',').map((cell) => cell.trim())
      );
      setSheetData(data);
      setSheets({ 'Sheet1': data });
      setActiveSheet('Sheet1');
    }
  }, [csvContent, blobUrl]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setTextContent(newContent);
    onContentChange(newContent);
  };

  const handleDataChange = useCallback((data: any[]) => {
    const csvContent = data
      .map((row: any[]) => row.map((cell) => `"${cell ?? ''}"`).join(','))
      .join('\n');
    setTextContent(csvContent);
    onContentChange(csvContent);
  }, [onContentChange]);

  // Sync horizontal scroll between table and bottom scrollbar
  const handleTableScroll = useCallback(() => {
    if (tableScrollRef.current && bottomScrollRef.current) {
      bottomScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
  }, []);

  const handleBottomScroll = useCallback(() => {
    if (tableScrollRef.current && bottomScrollRef.current) {
      tableScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    }
  }, []);

  // Show loading state
  if (isLoadingSheet || isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin mb-4">
            <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full"></div>
          </div>
          <p className="text-gray-500 dark:text-gray-400">{t('Loading spreadsheet...')}</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-2">{t('Error loading spreadsheet')}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  const currentSheetData = sheets[activeSheet] || [];
  const sheetNames = Object.keys(sheets);

  if (isPreviewMode) {
    return (
      <div className="w-full h-full flex flex-col bg-white dark:bg-gray-900">
        {/* Sticky header with tabs and toggle button */}
        <div className="flex-shrink-0 sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700 px-4 py-2 bg-white dark:bg-gray-900">
          <div className="flex items-center justify-between gap-4">
            {/* Sheet tabs */}
            {sheetNames.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {sheetNames.map((sheetName) => (
                  <button
                    key={sheetName}
                    onClick={() => {
                      setActiveSheet(sheetName);
                      setSheetData(sheets[sheetName] || []);
                    }}
                    className={`px-3 py-2 text-sm font-medium rounded whitespace-nowrap transition-colors ${
                      activeSheet === sheetName
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {sheetName}
                  </button>
                ))}
              </div>
            )}

            {/* Toggle button */}
            <button
              onClick={() => setIsPreviewMode(false)}
              className="ml-auto px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors whitespace-nowrap"
            >
              {t('Edit CSV')}
            </button>
          </div>
        </div>

        {/* Excel Preview Table container - vertical scrolling visible, horizontal hidden */}
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
          {/* Inner wrapper for horizontal scrolling - synced with bottom scrollbar */}
          <div
            ref={tableScrollRef}
            className="overflow-x-auto hide-scrollbar"
            onScroll={handleTableScroll}
          >
            <ExcelPreviewTable
              sheetData={currentSheetData}
              sheetName={activeSheet}
              showHeaders={true}
            />
          </div>
        </div>

        {/* Sticky horizontal scrollbar at bottom - outside scroll container */}
        <div className="flex-shrink-0 sticky bottom-0 z-10 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          <div
            ref={bottomScrollRef}
            className="overflow-x-auto scrollbar-only"
            onScroll={handleBottomScroll}
          >
            <div style={{ width: 'fit-content', minWidth: '100%' }}>
              <div style={{ height: '8px' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Sticky header with toggle */}
      <div className="flex-shrink-0 sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700 px-4 py-2 bg-white dark:bg-gray-900 flex items-center justify-between">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('CSV Mode - Edit directly or paste new CSV data')}
        </div>
        <button
          onClick={() => setIsPreviewMode(true)}
          className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors whitespace-nowrap"
        >
          {t('Preview Table')}
        </button>
      </div>

      {/* CSV textarea editor - scrolls only the content */}
      <textarea
        value={textContent}
        onChange={handleTextChange}
        className="flex-1 w-full p-4 font-mono text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 outline-none border-none resize-none overflow-auto custom-scrollbar-main"
        placeholder={t('Paste CSV content here or edit existing data\n\nExample:\nName,Age,City\nJohn,25,New York\nJane,30,Los Angeles')}
        spellCheck="false"
      />
    </div>
  );
};

export default ExcelEditor;
